import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser, Location } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { EMPTY, Subject } from 'rxjs';
import { filter, map, switchMap, takeUntil } from 'rxjs/operators';

import { CampaignCartResetService } from '../campaign/campaign-cart-reset.service';
import { CampaignScopeService } from '../campaign/campaign-scope.service';
import { clearCampaignLoginFlowTypes, writeCampaignLoginFlowTypes } from '../campaign/campaign-checkout-flag';
import { CampaignReadOutcome, CampaignReadService } from '../campaign/campaign-read.service';
import { ToastService } from '../../shared/services/toast.service';
import {
  clearStoredExternalChannelCampaign,
  persistExternalChannelCampaign,
  readStoredExternalChannelCampaign,
} from '../utils/external-channel-access.util';

import { I18nService } from './i18n.service';

interface RouteNode {
  data?: Record<string, unknown>;
  firstChild?: RouteNode | null;
}

interface CaptureRequest {
  code: string;
}

@Injectable({
  providedIn: 'root',
})
export class CampaignCaptureService {
  private readonly destroy$ = new Subject<void>();
  private readonly captures$ = new Subject<void>();
  private isInitialized = false;
  private readonly platformId = inject(PLATFORM_ID);
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly campaignRead = inject(CampaignReadService);
  private readonly toast = inject(ToastService);
  private readonly i18n = inject(I18nService);
  private readonly cartReset = inject(CampaignCartResetService);
  private readonly campaignScope = inject(CampaignScopeService);

  initialize(): void {
    if (!isPlatformBrowser(this.platformId) || this.isInitialized) {
      return;
    }

    this.isInitialized = true;

    this.captures$
      .pipe(
        map(() => this.snapshotRequest()),
        switchMap(request => {
          if (!request) {
            return EMPTY;
          }

          return this.campaignRead.read(request.code);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(outcome => this.apply(outcome));

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.captureFromCurrentUrl();
      });

    // The first read has to wait until the route is active. Reading earlier
    // treats the landing as an ordinary page and the strip navigation drops
    // the offers.
    if (this.router.navigated) {
      this.captureFromCurrentUrl();
    }
  }

  private captureFromCurrentUrl(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.keepCampaignInUrl();

    try {
      this.captures$.next();
    } catch {
      // ignore
    }
  }

  private snapshotRequest(): CaptureRequest | null {
    try {
      const params = new URLSearchParams(window.location.search);
      const code = (params.get('campaign') ?? '').trim();
      if (!code || this.isLandingRoute()) {
        return null;
      }

      return { code };
    } catch {
      return null;
    }
  }

  private apply(outcome: CampaignReadOutcome): void {
    if (this.isLandingRoute()) {
      return;
    }

    if (outcome.kind === 'active') {
      persistExternalChannelCampaign(outcome.campaign.code);
      writeCampaignLoginFlowTypes(outcome.campaign.flowTypes);
      this.cartReset.reset(outcome.campaign.code);
      this.campaignScope.invalidate();
      this.keepCampaignInUrl();
      return;
    }

    if (outcome.kind === 'notFound') {
      clearStoredExternalChannelCampaign();
      clearCampaignLoginFlowTypes();
      this.toast.showError(this.i18n.instant('shared.messages.campaignMissing'));
      this.router.navigate(['/'], { replaceUrl: true }).catch(() => {
        // The message and the cleared session already stand if navigation fails.
      });
    }
  }

  private isLandingRoute(): boolean {
    const state = this.router.routerState as { snapshot?: { root?: RouteNode | null } } | undefined;
    return hasCampaignLanding(state?.snapshot?.root);
  }

  private keepCampaignInUrl(): void {
    const stored = readStoredExternalChannelCampaign();
    if (!stored) {
      return;
    }

    const current = (new URLSearchParams(window.location.search).get('campaign') ?? '').trim();
    if (current) {
      return;
    }

    if (this.isLandingRoute()) {
      const params = new URLSearchParams(window.location.search);
      params.set('campaign', stored);
      const query = params.toString();
      const next = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
      window.history.replaceState(window.history.state, '', next);
      return;
    }

    try {
      const tree = this.router.parseUrl(this.location.path(true) || '/');
      tree.queryParams = { ...tree.queryParams, campaign: stored };
      this.router.navigateByUrl(tree, { replaceUrl: true }).catch(() => {
        // Putting the campaign back is best-effort after the route change.
      });
    } catch {
      // ignore
    }
  }
}

function hasCampaignLanding(route: RouteNode | null | undefined): boolean {
  let current = route;
  while (current) {
    if (current.data?.['campaignLanding'] === true) {
      return true;
    }
    current = current.firstChild ?? null;
  }
  return false;
}
